import styled from 'styled-components/macro';

export interface Props {
    className?: string;
    entityId: string;
}

import { ActionButton } from 'elements/eventView/atomic/ActionButton';
import { motion } from 'framer-motion';
import { AnimTap, EFonts } from 'libs/styles/styling';
import { useAnalytics } from 'libs/utils/site/analytics';
import { uniq } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { CtxEventView } from 'session/ctxEventView';
import { Localization } from 'static/localization/localization';

export const EntityMultifiltersRow = (props: Props) => {
    const ctxEventView = useContext(CtxEventView);
    const analytics = useAnalytics();

    const multifilters = ctxEventView.otherEntityDefinitions.find(
        (e) => e.id === props.entityId
    ).multifilters;
    const entityState = ctxEventView.entityStates[props.entityId];

    const allOptions = multifilters?.flatMap((e) => e.optionIds);
    const allOptionIds = allOptions?.map((e) => e.categoryId);

    function getInitialActiveOptionIds() {
        if (entityState.filterIds?.length) {
            return entityState.filterIds;
        }
        return allOptionIds || [];
    }

    const [activeOptionIds, setActiveOptionIds] = useState<string[]>(getInitialActiveOptionIds());

    useEffect(() => {
        entityState.setFilterIds(activeOptionIds);
    }, [activeOptionIds]);

    if ((!multifilters || !allOptionIds) ?? !allOptionIds.length) {
        return null;
    }

    function ensureShowOptions(optionIds: string[]) {
        setActiveOptionIds((curr) => uniq([...curr, ...optionIds]));

        analytics.track('event_ui.filter.clicked', {
            id: 'show-many',
            title: 'show-many',
            type: 'category'
        });
    }

    function ensureHideOptions(optionIds: string[]) {
        setActiveOptionIds((curr) => curr.filter((optionId) => !optionIds.includes(optionId)));

        analytics.track('event_ui.filter.clicked', {
            id: 'hide-many',
            title: 'hide-many',
            type: 'category'
        });
    }

    function toggleOption(optionId: string) {
        setActiveOptionIds((curr) =>
            curr.includes(optionId) ? curr.filter((id) => id !== optionId) : [...curr, optionId]
        );

        analytics.track('event_ui.filter.clicked', {
            id: optionId,
            title: allOptions.find((e) => e.categoryId === optionId)?.title,
            type: 'category'
        });
    }

    const selectedBgColor =
        ctxEventView?.eventData?.eventInfo?.styling?.circle?.bgColor ?? '#C57142';
    const selectedTextColor =
        ctxEventView?.eventData?.eventInfo?.styling?.circle?.iconColor ?? '#ffffff';

    const filterText = ctxEventView?.eventData?.eventInfo?.noNumbers
        ? Localization.FILTERS
        : `${Localization.FILTERS} (${entityState.visible?.length}/${entityState.all?.length})`;

    return (
        <S_EntityMultifiltersRow className={props.className}>
            <S_FirstRow>
                <ActionButton
                    highlighted
                    icon={{ iconName: entityState.multifiltersOpen ? 'arrow-up' : 'arrow-down' }}
                    text={filterText}
                    onClick={() => {
                        entityState.setMultifiltersOpen(!entityState.multifiltersOpen);
                    }}
                />
            </S_FirstRow>
            {entityState.multifiltersOpen && (
                <S_Expanded>
                    <S_Filters>
                        {multifilters.map((filter) => {
                            const allInFilterSelected = filter.optionIds.every((option) =>
                                activeOptionIds.includes(option.categoryId)
                            );
                            return (
                                <S_FilterSection key={filter.filterId}>
                                    <S_FilterSectionTopRow>
                                        <S_FilterTitle>{filter.title}</S_FilterTitle>
                                        <S_ToggleFilters
                                            onClick={() => {
                                                const allOptions = filter.optionIds.map(
                                                    (e) => e.categoryId
                                                );
                                                if (allInFilterSelected) {
                                                    ensureHideOptions(allOptions);
                                                } else {
                                                    ensureShowOptions(allOptions);
                                                }
                                            }}
                                            {...AnimTap}
                                        >
                                            {allInFilterSelected
                                                ? Localization.HIDE_ALL
                                                : Localization.SHOW_ALL}
                                        </S_ToggleFilters>
                                    </S_FilterSectionTopRow>

                                    <S_FilterOptionsInner>
                                        {filter.optionIds.map((option) => (
                                            <S_FilterOption
                                                key={option.categoryId}
                                                $active={activeOptionIds.includes(
                                                    option.categoryId
                                                )}
                                                $selectedBgColor={selectedBgColor}
                                                $selectedTextColor={selectedTextColor}
                                                onClick={() => {
                                                    toggleOption(option.categoryId);
                                                }}
                                                {...AnimTap}
                                            >
                                                {option.title}
                                            </S_FilterOption>
                                        ))}
                                    </S_FilterOptionsInner>
                                </S_FilterSection>
                            );
                        })}
                    </S_Filters>
                </S_Expanded>
            )}
        </S_EntityMultifiltersRow>
    );
};

export const S_EntityMultifiltersRow = styled.div`
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    background-color: #fafcfe;
    border-bottom-width: 1px;
    border-bottom-color: #e8f0f5;
    @media only screen and (min-width: 1260px) {
        background-color: unset;
        border: unset;
    }
`;

export const S_FirstRow = styled.div`
    display: flex;
    flex-direction: row;
    padding: 10px;
`;

export const S_Expanded = styled.div``;

export const S_Filters = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 10px 0px 10px;
`;

export const S_FilterSection = styled.div`
    padding: 13px 10px;
`;

export const S_FilterSectionTopRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 5px;
    @media only screen and (min-width: 1260px) {
        justify-content: flex-start;
        gap: 20px;
        align-items: center;
        margin: 0 0 10px 0;
    }
`;

export const S_FilterTitle = styled.div`
    font-family: ${EFonts.Medium};
    font-size: 15px;
    @media only screen and (min-width: 1260px) {
        font-family: ${EFonts.Bold};
        font-size: 16px;
    }
`;

export const S_ToggleFilters = styled(motion.div)`
    font-size: 14px;
    font-family: ${EFonts.Medium};
    color: #565656;
    text-decoration: underline;
    @media only screen and (min-width: 1260px) {
        cursor: pointer;
        padding: 8px 12px;
        border-radius: 5px;
        background-color: #eaf0f5;
        color: #041321;
        text-decoration: none;
    }
`;

export const S_FilterOptionsInner = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 5px;
`;

export const S_FilterOption = styled(motion.div)<{
    $active: boolean;
    $selectedBgColor: string;
    $selectedTextColor: string;
}>`
    border-radius: 5px;
    background-color: ${(props) => (props.$active ? props.$selectedBgColor : 'transparent')};
    color: ${(props) => (props.$active ? props.$selectedTextColor : '#000')};
    padding: 10px 12px;
    font-family: ${EFonts.Regular};
    font-size: 12px;
    cursor: pointer;
    @media only screen and (min-width: 1260px) {
        font-size: 13px;
        padding: 12px 14px;
    }
`;
